import { useCallback, useEffect, useRef, useState } from 'react'

const DIFFICULTY_LEVELS: { [difficulty: number]: { primeFactors: number; maxNumber: number; maxFactor: number } } = {
  1: {
    primeFactors: 2,
    maxNumber: 20,
    maxFactor: 10,
  },
  2: {
    primeFactors: 2 - 3,
    maxNumber: 40,
    maxFactor: 10,
  },
  3: {
    primeFactors: 3,
    maxNumber: 70,
    maxFactor: 15,
  },
  4: {
    primeFactors: 4,
    maxNumber: 150,
    maxFactor: 18,
  },
}

export interface FactorNegativeQuestions {
  sum: number
  factors: number[]
}

const getPrimes = (min: number, max: number): number[] => {
  const result = Array(max + 1)
    .fill(0)
    .map((_, i) => i)
  // eslint-disable-next-line
  for (let i = 2; i <= Math.sqrt(max + 1); i++) {
    // eslint-disable-next-line
    for (let j = i ** 2; j < max + 1; j += i) delete result[j]
  }
  return Object.values(result.slice(Math.max(min, 2)))
}

const getRandNum = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

const getRandPrime = (min: number, max: number): number => {
  const primes = getPrimes(min, max)
  return primes[getRandNum(0, primes.length - 1)]
}

const generateFactorNegativeSet = (difficulty: number): FactorNegativeQuestions => {
  const diff = DIFFICULTY_LEVELS[difficulty]
  const primeFactors = diff.primeFactors === 2 - 3 ? Math.floor(Math.random() * (3 - 2 + 1) + 2) : diff.primeFactors

  let num = 1
  let numFactors: number[] = []
  const mappingArray: number[] = [...Array(primeFactors)]
  mappingArray.map((_e, i): number => {
    const factor = getRandPrime(0, diff.maxFactor)
    if (num * factor < diff.maxNumber / diff.primeFactors) numFactors = [...numFactors, factor]
    else if (num * factor < diff.maxNumber / 2) {
      numFactors = [...numFactors, Math.floor(Math.random() * (3 - 2 + 1) + 2)]
    } else numFactors = [...numFactors, 2]

    num *= numFactors[i]
    return factor
  })

  numFactors = [...numFactors, -1]
  return { sum: (num *= -1), factors: numFactors }
}

export const useFactorNegative = (difficulty: number): [FactorNegativeQuestions, () => void] => {
  const [question, setQuestion] = useState<FactorNegativeQuestions>({ sum: 0, factors: [] })
  const taskHistory = useRef<number[]>([])
  const retry = useRef<number>(0)

  const generateQuestion = useCallback((): void => {
    const questionSet = generateFactorNegativeSet(difficulty)

    if (!taskHistory.current.includes(questionSet.sum) || retry.current >= 3) {
      taskHistory.current = [...taskHistory.current, questionSet.sum].slice(-3)
      setQuestion(questionSet)
      retry.current = 0
    } else {
      generateQuestion()
      retry.current += 1
    }
  }, [difficulty])

  useEffect(() => {
    generateQuestion()
  }, [generateQuestion])

  return [question, generateQuestion]
}
